import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown, ChevronUp, RefreshCcw, Plus, X,
  Activity, Search,
} from "lucide-react";
import { Button } from "components/ui/button";

interface CampaignNodeProps {
  node: any;
  editableNode?: any;
  isOpen: boolean;
  campaigns: any[];
  isRefreshing?: boolean;

  onToggleAccordion: () => void;
  onAddNode: (nodeData: any) => void;
  onUpdateCampaign: (nodeData: any) => void;
  onRefreshCampaigns: () => void;
  onDragStart?: (e: React.DragEvent, nodeData: any) => void;
}

const INPUT =
  "w-full rounded-md border border-line dark:border-white/[0.10] " +
  "bg-gray-50 dark:bg-white/[0.05] " +
  "text-xs text-gray-700 dark:text-gray-200 " +
  "placeholder-gray-400 dark:placeholder-gray-600 " +
  "px-2.5 py-1.5 " +
  "focus:outline-none focus:ring-1 focus:ring-indigo-400/70 focus:border-indigo-400/50 " +
  "disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-semibold text-content-subtle dark:text-gray-500 uppercase tracking-wide mb-0.5">
    {children}
  </p>
);

const CampaignNode: React.FC<CampaignNodeProps> = ({
  node,
  editableNode,
  isOpen,
  campaigns,
  isRefreshing = false,
  onToggleAccordion,
  onAddNode,
  onUpdateCampaign,
  onRefreshCampaigns,
  onDragStart,
}) => {
  const today = new Date().toISOString().split("T")[0];

  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");

  const [campaignName, setCampaignName] = useState("");
  const [subject, setSubject] = useState("");
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [minEndDate, setMinEndDate] = useState<string | null>(null);
  const [campaignNameError, setCampaignNameError] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isNonEditable, setIsNonEditable] = useState(false);
  const [nodeId, setNodeId] = useState<number | null>(null);

  useEffect(() => {
    if (!editableNode) { setIsEditMode(false); return; }
    setNodeId(editableNode.id ?? null);
    setCampaignName(editableNode.name ?? "");
    setSubject(editableNode.subject ?? "");
    setStartDate(editableNode.startDate ?? null);
    setEndDate(editableNode.endDate ?? null);
    setIsEditMode(!editableNode.isNonEditable);
    setIsNonEditable(!!editableNode.isNonEditable);
    setShowForm(true);
    if (!isOpen) onToggleAccordion();
  }, [editableNode]);

  const filteredCampaigns = useMemo(
    () =>
      search.trim()
        ? campaigns.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()))
        : campaigns,
    [campaigns, search]
  );

  const isFormComplete = useMemo(
    () =>
      !!campaignName && !!subject && !!startDate && !!endDate &&
      !campaignNameError && !subjectError && !endDateError,
    [campaignName, subject, startDate, endDate, campaignNameError, subjectError, endDateError]
  );

  const updateEndDateMin = () => {
    if (!startDate) return;
    const s = new Date(startDate);
    s.setDate(s.getDate() + 1);
    const min = s.toISOString().split("T")[0];
    setMinEndDate(min);
    if (endDate && new Date(endDate) <= s) setEndDateError("End date must be after start date.");
    else setEndDateError("");
  };

  const clearForm = () => {
    setCampaignName(""); setSubject(""); setStartDate(null); setEndDate(null);
    setIsEditMode(false); setIsNonEditable(false);
    setCampaignNameError(""); setSubjectError(""); setEndDateError("");
    setNodeId(null);
    setShowForm(false);
  };

  const handleNodeAction = (e?: React.DragEvent, kind: "add" | "drag" = "add") => {
    const nodeData = { name: campaignName || node.name, subject, startDate, endDate, icon: node.icon, type: "campaign", isNonEditable: true };
    if (kind === "drag" && e) e.dataTransfer.setData("application/json", JSON.stringify(nodeData));
    else onAddNode(nodeData);
    setIsEditMode(false);
  };

  const updateNode = () => {
    if (!isFormComplete || isNonEditable) return;
    onUpdateCampaign({ id: nodeId, name: campaignName, subject, startDate, endDate, icon: node.icon, type: "campaign" });
    setIsEditMode(false);
    clearForm();
  };

  const dragStartExisting = (e: React.DragEvent, campaign: any) => {
    const nodeData = { id: campaign.id, name: campaign.name, startDate: campaign.s_date?.split(" ")[0], endDate: campaign.e_date?.split(" ")[0], type: "campaign", isEditable: false, hideSubject: true };
    e.dataTransfer.setData("application/json", JSON.stringify(nodeData));
    onDragStart?.(e, nodeData);
  };

  const addExisting = (campaign: any) => {
    onAddNode({ id: campaign.id, name: campaign.name, subject: campaign.subject, startDate: campaign.s_date?.split(" ")[0], endDate: campaign.e_date?.split(" ")[0], icon: node.icon, type: "campaign", isEditable: false, hideSubject: true });
  };

  return (
    <div className="border border-line dark:border-white/[0.08] rounded-xl overflow-hidden shadow-sm mt-2 bg-surface dark:bg-dark-800">

      <div
        onClick={() => !isEditMode && onToggleAccordion()}
        className="flex justify-between items-center px-3 py-2.5 cursor-pointer
                   bg-gray-50 dark:bg-white/[0.04] hover:bg-gray-100 dark:hover:bg-white/[0.07]
                   border-b border-line dark:border-white/[0.06] transition-colors"
      >
        <div className="flex items-center gap-2 font-semibold text-gray-700 dark:text-gray-200 text-sm">
          <Activity className="h-4 w-4 text-blue-500 dark:text-blue-400" />
          <span>{node.name}</span>
        </div>

        <div className="flex items-center gap-0.5">
          <Button
            onClick={e => { e.stopPropagation(); onRefreshCampaigns(); }}
            variant="ghost" size="icon"
            className="text-content-subtle dark:text-gray-300 hover:text-emerald-500 dark:hover:text-emerald-400 h-7 w-7"
            title="Refresh"
          >
            <RefreshCcw className={`h-3.5 w-3.5 transition-transform ${isRefreshing ? "animate-spin" : ""}`} />
          </Button>

          {isOpen && (
            <Button
              onClick={e => { e.stopPropagation(); setShowForm(f => !f); }}
              variant="ghost" size="icon"
              className={`h-7 w-7 transition-colors ${showForm
                ? "text-indigo-500 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
                : "text-content-subtle dark:text-gray-300 hover:text-indigo-500 dark:hover:text-indigo-400"
                }`}
              title={showForm ? "Back to list" : "Create New Campaign"}
            >
              {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          )}

          {isOpen
            ? <ChevronUp className="h-4 w-4 text-content-subtle dark:text-gray-300 ml-0.5" />
            : <ChevronDown className="h-4 w-4 text-content-subtle dark:text-gray-300 ml-0.5" />}
        </div>
      </div>

      {isOpen && (
        <div className="p-2 space-y-1.5">

          {!showForm && (
            <>
              <div className="flex items-center gap-1.5">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-content-subtle dark:text-gray-600 pointer-events-none" />
                  <input
                    type="text" placeholder="Search…" value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-6 pr-5 py-1 text-[11px] rounded-md
                               bg-gray-50 dark:bg-white/[0.05]
                               border border-line dark:border-white/[0.08]
                               text-gray-700 dark:text-gray-300
                               placeholder-gray-400 dark:placeholder-gray-600
                               focus:outline-none focus:ring-1 focus:ring-indigo-400/60
                               transition-colors"
                  />
                  {search && (
                    <button onClick={() => setSearch("")}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-secondary">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
                {!isRefreshing && (
                  <span className="flex-shrink-0 text-[9.5px] text-content-subtle dark:text-gray-600 tabular-nums">
                    {filteredCampaigns.length}/{campaigns.length}
                  </span>
                )}
              </div>

              {/* Campaign list */}
              <div
                className="overflow-y-auto rounded-md border border-line-subtle dark:border-white/[0.06]
                           [&::-webkit-scrollbar]:w-[3px]
                           [&::-webkit-scrollbar-thumb]:rounded-full
                           [&::-webkit-scrollbar-thumb]:bg-indigo-300/40
                           dark:[&::-webkit-scrollbar-thumb]:bg-indigo-600/40"
                style={{ maxHeight: 168 }}
              >
                {isRefreshing ? (
                  (["68%", "52%", "78%", "60%"] as const).map((w, i) => (
                    <div key={i} className="sidebar-skeleton-row border-b border-line-subtle dark:border-white/[0.04] last:border-0">
                      <div className="sidebar-skeleton-line" style={{ width: w }} />
                      <div className="sidebar-skeleton-dot" />
                    </div>
                  ))
                ) : filteredCampaigns.length > 0 ? (
                  filteredCampaigns.map((c, idx) => (
                    <div
                      key={c.id}
                      draggable
                      onDragStart={e => dragStartExisting(e, c)}
                      className={`group flex items-center justify-between px-2 py-[5px]
                                 hover:bg-indigo-50 dark:hover:bg-indigo-900/10
                                 cursor-grab active:cursor-grabbing transition-colors
                                 ${idx < filteredCampaigns.length - 1 ? "border-b border-line-subtle dark:border-white/[0.04]" : ""}`}
                    >
                      <span className="text-micro font-medium text-content-secondary dark:text-gray-300 truncate leading-none">
                        {c.name}
                      </span>
                      <button
                        onClick={() => addExisting(c)}
                        title="Add to canvas"
                        className="flex-shrink-0 ml-1.5 w-[18px] h-[18px] rounded-full flex items-center justify-center
                                   opacity-80 group-hover:opacity-100
                                   bg-gray-100 dark:bg-white/[0.08]
                                   group-hover:bg-indigo-500 dark:group-hover:bg-indigo-600
                                   text-content-muted dark:text-gray-400 group-hover:text-white transition-all"
                      >
                        <Plus className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-[11px] text-content-subtle dark:text-gray-600 italic text-center py-4">
                    {search ? `No results for "${search}"` : "No campaigns found."}
                  </p>
                )}
              </div>
            </>
          )}

          {showForm && (
            <div className="space-y-2">

              {/* Campaign Name */}
              <div>
                <Label>Campaign Name</Label>
                <input
                  type="text" placeholder="e.g. Diwali Campaign 2025"
                  className={INPUT} value={campaignName}
                  onChange={e => setCampaignName(e.target.value)}
                  disabled={isNonEditable}
                />
                {!!campaignNameError && <p className="text-[10px] text-red-500 dark:text-red-400 mt-0.5">{campaignNameError}</p>}
              </div>

              {/* Subject */}
              <div>
                <Label>Subject</Label>
                <input
                  type="text" placeholder="Email subject line"
                  className={INPUT} value={subject}
                  onChange={e => setSubject(e.target.value)}
                  disabled={isNonEditable}
                />
                {!!subjectError && <p className="text-[10px] text-red-500 dark:text-red-400 mt-0.5">{subjectError}</p>}
              </div>

              {/* Start + End date — side by side */}
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label>Start Date</Label>
                  <input
                    type="date" className={INPUT}
                    value={startDate ?? ""} min={today}
                    onChange={e => setStartDate(e.target.value)}
                    onBlur={updateEndDateMin} disabled={isNonEditable}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <input
                    type="date" className={INPUT}
                    value={endDate ?? ""} min={minEndDate ?? ""}
                    onChange={e => setEndDate(e.target.value)}
                    disabled={isNonEditable}
                  />
                  {!!endDateError && <p className="text-[10px] text-red-500 dark:text-red-400 mt-0.5">{endDateError}</p>}
                </div>
              </div>

              {/* Actions */}
              {!isNonEditable && isEditMode ? (
                <div className="flex gap-1.5 pt-0.5">
                  <Button variant="primary" onClick={updateNode} className="flex-1 h-7 text-xs">Update</Button>
                  <Button variant="secondary" onClick={clearForm} className="flex-1 h-7 text-xs">Cancel</Button>
                </div>
              ) : isFormComplete && !isEditMode && !isNonEditable ? (
                <Button
                  draggable
                  onDragStart={e => handleNodeAction(e, "drag")}
                  onClick={() => handleNodeAction()}
                  variant="primary"
                  className="w-full h-7 text-xs gap-1.5 pt-0.5"
                >
                  <Plus className="w-3 h-3" />
                  Add to Canvas
                </Button>
              ) : null}

            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default CampaignNode;
